import { useState, useCallback } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useAIConfig } from "../contexts/AIConfigContext";
import styles from "./AIStatusBanner.module.css";

const DISMISS_KEY = "verso:ai-banner-dismissed";

interface AIStatusBannerProps {
  message?: string;
  onClick?: () => void;
}

export function AIStatusBanner({ message: _message, onClick }: AIStatusBannerProps) {
  const { t } = useTranslation();
  const { byokAllowed } = useAIConfig();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "true";
    } catch {
      return false;
    }
  });

  const handleDismiss = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, "true");
    } catch {
      // storage unavailable — dismiss for this session only
    }
  }, []);

  if (dismissed) return null;

  const label = byokAllowed ? t("aiBanner.connectAI") : t("aiBanner.enterpriseSetupAI");

  const handleClick = byokAllowed
    ? onClick
    : () => {
        toast(t("aiBanner.enterpriseDescription"), { id: "enterprise-upsell" });
      };

  return (
    <span className={styles.wrapper}>
      <button
        type="button"
        className={styles.indicator}
        onClick={handleClick}
        title={label}
        aria-label={label}
        style={byokAllowed ? undefined : { opacity: 0.6 }}
      >
        <svg
          className={styles.icon}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8 1L9.5 5.5L14 7L9.5 8.5L8 13L6.5 8.5L2 7L6.5 5.5L8 1Z"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinejoin="round"
          />
        </svg>
        <span className={styles.label}>{label}</span>
      </button>
      <button
        type="button"
        className={styles.dismissButton}
        onClick={handleDismiss}
        aria-label={t("aiBanner.dismissBanner", "Dismiss")}
        title={t("aiBanner.dismissBanner", "Dismiss")}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3 3L9 9M9 3L3 9"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </span>
  );
}

/**
 * Reset the AI banner dismissal (e.g. when AI settings change).
 */
export function resetAIBannerDismissal(): void {
  try {
    localStorage.removeItem(DISMISS_KEY);
  } catch {
    // noop
  }
}
